import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";
const STORAGE_KEY = "eolass-theme";

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function storedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" ? value : null;
}

function applyTheme(theme: Theme | null) {
  // null means "no explicit choice — follow the system" — same convention
  // the inline script in public/index.html uses to avoid a flash of the
  // wrong theme on first paint.
  if (theme) {
    document.documentElement.setAttribute("data-theme", theme);
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

/**
 * Tracks the site's light/dark theme. Defaults to the OS preference; an
 * explicit choice (from toggleTheme) overrides that and persists across
 * visits. `theme` is always the *resolved* value actually being shown.
 */
export function useTheme() {
  const [explicit, setExplicit] = useState<Theme | null>(() => storedTheme());
  const [theme, setTheme] = useState<Theme>(() => explicit ?? (systemPrefersDark() ? "dark" : "light"));

  useEffect(() => {
    setTheme(explicit ?? (systemPrefersDark() ? "dark" : "light"));
    applyTheme(explicit);
  }, [explicit]);

  // Follow the OS preference live, but only while there's no explicit choice.
  useEffect(() => {
    if (explicit) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setTheme(systemPrefersDark() ? "dark" : "light");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [explicit]);

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    window.localStorage.setItem(STORAGE_KEY, next);
    setExplicit(next);
  }, [theme]);

  return { theme, toggleTheme };
}
